import React, { useEffect, useState, useRef, useCallback } from "react";
import "./AddEditOrder.scss";
import CustomerInformation from "./CustomerInformation/CustomerInformation";
import ShipmentInformation from "./ShipmentInformation/ShipmentInformation";
import ServiceInformation from "./ServiceInformation/ServiceInformation";
import SendInformation from "./SendInformation/SendInformation";
import OrderPrint from "./OrderPrint/OrderPrint";
import _axios from "../../../../httpService";
import { prefixUrl } from "../../../../utils/utilsData";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
  validateStep1,
  validateStep2,
} from "./ValidationComponent/ValidationComponent";
import Loader from "../../../Loader";
import { useReactToPrint } from "react-to-print";

const STORAGE_KEY = "addEditOrderDraft_v1";
const EDIT_DRAFT_KEY = "editOrder_tempDraft_v1";

const initialSendData = {
  id_moshtari: "",
  date_sabt: "",
  time_sabt: "",
  date_amade: "",
  time_amade: "",
  date_tahvil: "",
  time_tahvil: "",
  name_moshtari: "",
  state: "",
  city: "",
  address: "",
  mobile_moshtari: "",
  percent_profit: "",
  products: [],
  services: [],
  cars: [],
};

const AddEditOrder = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [orderId, setOrderId] = useState(location.state?.id || null);
  const [isEditMode, setIsEditMode] = useState(location.state?.mode === "edit");
  const [isViewMode, setIsViewMode] = useState(location.state?.mode === "view");

  const [currentStep, setCurrentStep] = useState(
    location.state?.mode === "view" ? 5 : 1,
  );
  const [sendData, setSendData] = useState(initialSendData);
  const [selectData, setSelectData] = useState({
    moshtari: [],
    products: [],
    services: [],
    cars: [],
  });

  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const justReturnedFromDriver = useRef(false);
  const printRef = useRef(null);

  const steps = [
    "اطلاعات مشتری",
    "اطلاعات مرسوله",
    "اطلاعات خدمات",
    "اطلاعات ارسال",
    "پرینت سفارش",
  ];

  // -----------------------------------------------------------------
  // 1. دریافت داده‌های اولیه (مشتری‌ها، محصولات، خدمات، ماشین‌ها)
  // -----------------------------------------------------------------
  const fetchInitialData = async () => {
    try {
      const res = await _axios.get(prefixUrl() + "/cheeka/submit_order/");
      if (res?.data?.status) {
        setSelectData(res.data.data);
        setIsInitialDataLoaded(true);
      } else {
        toast.error(res.data.message || "خطا در دریافت اطلاعات اولیه");
      }
    } catch (err) {
      toast.error("خطا در ارتباط با سرور");
    }
  };

  useEffect(() => {
    if (!isInitialDataLoaded) fetchInitialData();
  }, [isInitialDataLoaded]);

  // -----------------------------------------------------------------
  // 2. برگشت از صفحه افزودن راننده
  // -----------------------------------------------------------------
  const handleReturnFromDriver = useCallback(() => {
    justReturnedFromDriver.current = true;
    navigate(location.pathname, { replace: true, state: {} });

    const newCar = location.state?.newCar;
    if (!newCar) return;

    let restoredData = { ...initialSendData };

    // اول چک کن ببین از ویرایش اومده یا ایجاد جدید
    const editDraft = sessionStorage.getItem(EDIT_DRAFT_KEY);
    if (editDraft) {
      sessionStorage.removeItem(EDIT_DRAFT_KEY);
      const draft = JSON.parse(editDraft);
      setIsEditMode(true);
      setOrderId(draft.orderId);
      restoredData = draft.sendData;
    } else {
      const createDraft = sessionStorage.getItem(STORAGE_KEY);
      if (createDraft) restoredData = JSON.parse(createDraft).sendData;
    }

    // اضافه کردن ماشین جدید (اگر تکراری نبود)
    const exists = restoredData.cars.some((c) => +c.id_car === +newCar.id_car);
    if (!exists) restoredData.cars.push(newCar);

    setSendData(restoredData);
    setCurrentStep(4);
    setIsReady(true);
  }, [location.state?.newCar, location.pathname, navigate]);

  useEffect(() => {
    if (location.state?.newCar) {
      handleReturnFromDriver();
    }
  }, [location.state?.newCar, handleReturnFromDriver]);

  // -----------------------------------------------------------------
  // 3. حالت ویرایش: دریافت اطلاعات سفارش
  // -----------------------------------------------------------------
  const fetchEditOrderData = async (id) => {
    try {
      const res = await _axios.get(
        `${prefixUrl()}/cheeka/submit_order/?id_havale=${id}`,
      );
      if (res?.data?.status) {
        setSendData(res.data.data);
      } else {
        toast.error(res.data.message || "خطا در دریافت اطلاعات سفارش");
      }
    } catch (err) {
      toast.error("خطا در دریافت اطلاعات ویرایش");
    } finally {
      setIsReady(true);
    }
  };

  useEffect(() => {
    if (
      (isEditMode || isViewMode) &&
      orderId &&
      !isReady &&
      !location.state?.newCar
    ) {
      fetchEditOrderData(orderId);
    }
  }, [isEditMode, isViewMode, orderId, isReady, location.state?.newCar]);

  // -----------------------------------------------------------------
  // 4. حالت ایجاد جدید: بازگردانی draft
  // -----------------------------------------------------------------
  useEffect(() => {
    if (isEditMode || justReturnedFromDriver.current || isReady) return;

    const draftJSON = sessionStorage.getItem(STORAGE_KEY);
    if (!draftJSON) {
      setIsReady(true);
      return;
    }

    Swal.fire({
      title: "اطلاعات قبلی پیدا شد",
      text: "آیا می‌خواهید اطلاعات قبلی که وارد کردید بازگردانده شود؟",
      icon: "question",
      showDenyButton: true,
      confirmButtonText: "بله، بازگردانی کن",
      denyButtonText: "نه، حذف کن",
      reverseButtons: true,
      customClass: {
        confirmButton: "btn btn-success me-3",
        denyButton: "btn btn-danger",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        const draft = JSON.parse(draftJSON);
        setSendData(draft.sendData);
        setCurrentStep(draft.currentStep || 1);
        toast.success("اطلاعات قبلی با موفقیت بازگردانی شد");
      } else if (result.isDenied) {
        sessionStorage.removeItem(STORAGE_KEY);
        toast.info("اطلاعات قبلی حذف شد");
      }
      setIsReady(true);
    });
  }, [isEditMode, isReady]);

  // -----------------------------------------------------------------
  // 5. ذخیره خودکار draft (فقط در حالت ایجاد)
  // -----------------------------------------------------------------
  useEffect(() => {
    if (isEditMode || !isReady) return;

    const hasData = Object.values(sendData).some((value) =>
      Array.isArray(value) ? value.length > 0 : !!value,
    );

    if (hasData) {
      const draft = { sendData, currentStep, savedAt: Date.now() };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
    }
  }, [sendData, currentStep, isEditMode, isReady]);

  // -----------------------------------------------------------------
  // پرینت و بازگشت
  // -----------------------------------------------------------------
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `سفارش_${sendData?.id_havale || orderId || "جدید"}`,
    copyStyles: true,
    onAfterPrint: () => {
      if (justReturnedFromDriver.current) {
        justReturnedFromDriver.current = false;
        navigate(-2);
      } else {
        navigate(-1);
      }
    },
  });

  // -----------------------------------------------------------------
  // ثبت نهایی سفارش
  // -----------------------------------------------------------------
  const handleSubmit = async (shouldPrint = true) => {
    const payload = {
      ...sendData,
      cars: sendData.cars.map((c) => ({
        id_car: c.id_car,
        car_name: c.car_name,
        driver_name: c.driver_name,
        pelak: c.pelak,
      })),
    };

    if (!isEditMode) delete payload.id_havale;
    if (isEditMode) payload.id_havale = orderId;

    try {
      const req = _axios.post(`${prefixUrl()}/cheeka/submit_order/`, payload);
      toast.promise(req, {
        pending: isEditMode
          ? "در حال بروزرسانی سفارش..."
          : "در حال ثبت سفارش...",
      });

      const res = await req;
      if (res.data.status) {
        sessionStorage.removeItem(STORAGE_KEY);
        toast.success(res.data.message);
        if (shouldPrint) {
          setTimeout(() => handlePrint(), 500);
        } else {
          navigate(-1);
        }
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error("خطا در ارتباط با سرور");
    }
  };

  // -----------------------------------------------------------------
  // جابجایی مراحل
  // -----------------------------------------------------------------
  const goNext = () => {
    if (currentStep === 1 && !validateStep1(sendData)) return;
    if (currentStep === 2 && !validateStep2(sendData)) return;
    if (currentStep === 5) return handleSubmit(true);

    setCurrentStep((prev) => Math.min(prev + 1, 5));
  };

  const goBack = () => {
    if (currentStep === 1 || (isViewMode && currentStep === 5)) navigate(-1);
    else setCurrentStep((prev) => prev - 1);
  };

  const navigateToAddDriver = () => {
    if (isEditMode) {
      sessionStorage.setItem(
        EDIT_DRAFT_KEY,
        JSON.stringify({ sendData, orderId }),
      );
    }
    navigate("/add-edit-driver", { state: { fromOrder: true } });
  };

  // -----------------------------------------------------------------
  // رندر مرحله فعلی
  // -----------------------------------------------------------------
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <CustomerInformation
            sendData={sendData}
            setSendData={setSendData}
            isEditMode={isEditMode}
            moshtariOptions={selectData.moshtari}
          />
        );
      case 2:
        return (
          <ShipmentInformation
            sendData={sendData}
            setSendData={setSendData}
            productOptions={selectData.products}
          />
        );
      case 3:
        return (
          <ServiceInformation
            sendData={sendData}
            setSendData={setSendData}
            serviceOptions={selectData.services}
          />
        );
      case 4:
        return (
          <SendInformation
            sendData={sendData}
            setSendData={setSendData}
            carOptions={selectData.cars}
            onAddNewDriver={navigateToAddDriver}
          />
        );
      case 5:
        return <OrderPrint ref={printRef} sendData={sendData} />;
      default:
        return null;
    }
  };

  // -----------------------------------------------------------------
  // اگر هنوز آماده نیست لودر نشون بده
  // -----------------------------------------------------------------
  if (!isReady) return <Loader />;

  return (
    <section className="add__edit__order">
      <div className="content__section">
        <div className="wizard">
          {steps.map((label, index) => {
            const stepNumber = index + 1;
            const isActive = stepNumber <= currentStep;
            const isCompleted = stepNumber < currentStep;

            return (
              <div className="wizard__step" key={stepNumber}>
                <div
                  className={`wizard__circle ${
                    isActive ? "wizard__circle--active" : ""
                  }`}
                >
                  {stepNumber}
                </div>
                <span
                  className={`wizard__label ${
                    isActive ? "wizard__label--active" : ""
                  }`}
                >
                  {label}
                </span>
                {stepNumber < 5 && (
                  <div
                    className={`wizard__line ${
                      isCompleted ? "wizard__line--active" : ""
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>

        {renderCurrentStep()}
      </div>

      <div className="bottom__section">
        <div className="action__buttons">
          <button className="cancel__button" onClick={goBack}>
            {currentStep === 1 ? "انصراف" : "بازگشت"}
          </button>
          {currentStep === 5 ? (
            <>
              {!isViewMode && (
                <button
                  className="submit__button"
                  style={{ backgroundColor: "#6c757d" }}
                  onClick={() => handleSubmit(false)}
                >
                  {isEditMode ? "ویرایش نهایی" : "ثبت نهایی"}
                </button>
              )}
              <button
                className="submit__button"
                onClick={() =>
                  isViewMode ? handlePrint() : handleSubmit(true)
                }
              >
                {isViewMode
                  ? "پرینت"
                  : isEditMode
                    ? "ویرایش و پرینت"
                    : "ثبت و پرینت"}
              </button>
            </>
          ) : (
            <button className="submit__button" onClick={goNext}>
              مرحله بعد
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default AddEditOrder;
